import { mediacoreApi } from '@apis/mediacoreApi'
import {
  createOneMutation,
  getAllByClientIdQuery,
  getByIdAndClientIdQuery,
  pkSubEndpointMutation,
  updateByIdMutation,
} from '@apis/mediacoreQueries'
import { SiteCompiler } from 'types-site'

const siteCompilersUrl = 'site/sitios-compiladores'

const siteCompilersApi = mediacoreApi.injectEndpoints({
  endpoints: (builder) => ({
    getSiteCompilerById: builder.query<SiteCompiler, { id: number; clientId: number }>({
      query: getByIdAndClientIdQuery(siteCompilersUrl),
      providesTags: (_result, _error, { id }) => [{ type: 'SiteCompiler', id }],
    }),
    getSiteCompilers: builder.query<SiteCompiler[], { clientId: number }>({
      query: getAllByClientIdQuery(siteCompilersUrl),
      providesTags: ['SiteCompiler'],
    }),
    createSiteCompiler: builder.mutation<SiteCompiler, { data: Partial<SiteCompiler> }>({
      query: createOneMutation(siteCompilersUrl),
      async onQueryStarted(_data, { dispatch, queryFulfilled }) {
        try {
          const { data: createdCompiler } = await queryFulfilled
          dispatch(
            siteCompilersApi.util.updateQueryData(
              'getSiteCompilers',
              { clientId: createdCompiler.client! },
              (draftCompilers) => {
                draftCompilers.unshift(createdCompiler)
                return draftCompilers
              },
            ),
          )
        } catch {}
      },
    }),
    updateSiteCompiler: builder.mutation<SiteCompiler, { id: number; data: Partial<SiteCompiler> }>({
      query: updateByIdMutation(siteCompilersUrl),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedCompiler } = await queryFulfilled
          dispatch(
            siteCompilersApi.util.updateQueryData(
              'getSiteCompilerById',
              { id, clientId: updatedCompiler.client! },
              (draft) => {
                Object.assign(draft, updatedCompiler)
              },
            ),
          )
          dispatch(
            siteCompilersApi.util.updateQueryData(
              'getSiteCompilers',
              { clientId: updatedCompiler.client! },
              (draftCompilers) => {
                return draftCompilers.map((draftCompiler) =>
                  draftCompiler.id !== id ? draftCompiler : updatedCompiler,
                )
              },
            ),
          )
        } catch {}
      },
    }),
    uploadSiteCompilerImages: builder.mutation<
      SiteCompiler,
      {
        id: number
        data: {
          files: File[]
          file_name: string //
          field: string
        }
      }
    >({
      query: pkSubEndpointMutation('site/sitios-compiladores', 'galeria'),
      invalidatesTags: ['SiteCompiler'],
    }),
    uploadSiteCompilerPrehomeImages: builder.mutation<
      SiteCompiler,
      {
        id: number
        data: {
          files: File[]
          file_name: string //
          field: string
        }
      }
    >({
      query: pkSubEndpointMutation('site/sitios-compiladores', 'galeria_prehome'),
      invalidatesTags: ['SiteCompiler'],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetSiteCompilerByIdQuery,
  useGetSiteCompilersQuery,
  useCreateSiteCompilerMutation,
  useUpdateSiteCompilerMutation,
  usePrefetch: usePrefetchSiteCompilers,
  useUploadSiteCompilerImagesMutation,
  useUploadSiteCompilerPrehomeImagesMutation,
} = siteCompilersApi
